import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "../lib/api";
import { Panel, Spinner, EmptyState, Field, inputCls } from "../components/common";
import { useLookup } from "./ResourceManager";

const STATUSES = [["Present", "bg-emerald-600"], ["Late", "bg-amber-500"], ["Absent", "bg-red-600"], ["Excused", "bg-slate-500"]];
const today = () => new Date().toLocaleDateString("en-CA");

export default function Attendance() {
  const classes = useLookup("classes", "&sort=-start_date");
  const [cid, setCid] = useState("");
  const [d, setD] = useState(null);
  const [day, setDay] = useState(today());
  const [rec, setRec] = useState({});
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => cid && api.get(`/attendance/class/${cid}`).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e))), [cid]);
  useEffect(() => { setD(null); load(); }, [load]);
  useEffect(() => {
    const s = d?.sessions.find((x) => x.date === day);
    setRec(s?.records || {});
    setNotes(s?.notes || "");
  }, [d, day]);
  const markAll = () => setRec(Object.fromEntries(d.roster.map((r) => [r.user_id, "Present"])));
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/attendance/class/${cid}`, { date: day, records: rec, notes });
      toast.success(data.alerts ? `Attendance saved — admissions alerted about ${data.alerts} student(s) with 2 absences in a row` : "Attendance saved");
      load();
    }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const rate = (uid) => {
    const rows = (d?.sessions || []).map((s) => s.records?.[uid]).filter(Boolean);
    return rows.length ? `${Math.round((rows.filter((x) => x === "Present" || x === "Late").length / rows.length) * 100)}%` : "—";
  };
  return (
    <div className="space-y-5" data-testid="attendance-page">
      <h1 className="font-heading text-2xl font-extrabold">Attendance</h1>
      <div className="flex flex-wrap items-end gap-3">
        <Field label="Class"><select className={`${inputCls} min-w-[280px]`} value={cid} onChange={(e) => setCid(e.target.value)} data-testid="attendance-class-select"><option value="">Select a class</option>{classes.map((c) => <option key={c.id} value={c.id}>{`${c.name} (${fmtDate(c.start_date)})`}</option>)}</select></Field>
        {cid && <Field label="Session date"><input type="date" className={inputCls} value={day} onChange={(e) => setDay(e.target.value)} data-testid="attendance-date" /></Field>}
      </div>
      {!cid ? <EmptyState>Choose a class to take attendance.</EmptyState> : !d ? <Spinner /> : (
        <div className="grid gap-6 xl:grid-cols-3">
          <Panel title={`Attendance sheet — ${fmtDate(day)}`} className="xl:col-span-2" action={d.roster.length > 0 && <button onClick={markAll} className="text-sm font-semibold text-red-600" data-testid="attendance-mark-all">Mark all present</button>}>
            {!d.roster.length ? <EmptyState>No students are enrolled in this class yet.</EmptyState> : (
              <>
                <div className="divide-y divide-slate-100">
                  {d.roster.map((r, i) => (
                    <div key={r.user_id} className="flex flex-wrap items-center justify-between gap-3 py-3" data-testid={`attendance-row-${i}`}>
                      <div><p className="font-semibold">{r.student_name}</p><p className="text-xs text-slate-500">Attendance rate: {rate(r.user_id)}</p></div>
                      <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label={`Attendance for ${r.student_name}`}>
                        {STATUSES.map(([s, c]) => (
                          <button key={s} role="radio" aria-checked={rec[r.user_id] === s} onClick={() => setRec({ ...rec, [r.user_id]: s })} data-testid={`attendance-${s.toLowerCase()}-${i}`}
                            className={`rounded-lg px-3 py-1.5 text-xs font-bold transition-colors ${rec[r.user_id] === s ? `${c} text-white` : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{s}</button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                <textarea rows={2} className={`${inputCls} mt-4`} placeholder="Session notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="attendance-notes" />
                <button disabled={busy} onClick={save} className="btn-primary btn-sm mt-3" data-testid="attendance-save">{busy ? "Saving…" : "Save attendance"}</button>
              </>
            )}
          </Panel>
          <Panel title="Past sessions" testid="attendance-history">
            {d.sessions.length ? d.sessions.map((s) => {
              const v = Object.values(s.records || {});
              return (
                <button key={s.id} onClick={() => setDay(s.date)} className={`flex w-full justify-between rounded-lg px-2 py-2 text-left text-sm hover:bg-slate-50 ${s.date === day ? "bg-slate-100" : ""}`}>
                  <span className="font-semibold">{fmtDate(s.date)}</span>
                  <span className="text-xs text-slate-500">{v.filter((x) => x === "Present" || x === "Late").length}/{v.length} attended</span>
                </button>
              );
            }) : <EmptyState>No sessions recorded yet.</EmptyState>}
          </Panel>
        </div>
      )}
    </div>
  );
}
